import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { jwtDecode } from 'jwt-decode';
import { Modal, Button } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useLoading } from '../../context/LoadingContext';
import config from '../../aws-exports';
import '../../styles/general.css';
import '../../styles/seller-farmer.css';

const SellerFarmerDashboard = () => {
  const [queryDescription, setQueryDescription] = useState('');
  const [activeTab, setActiveTab] = useState('basicInfo');
  const [activeUser, setActiveUser] = useState('');
  const [sellerItemDetails, setSellerItemDetails] = useState([]);
  const [queries, setQueries] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [queryPage, setQueryPage] = useState(1);
  const [queryTotalPages, setQueryTotalPages] = useState(1);
  const [selectedSellerItemDetail, setSelectedSellerItemDetail] = useState(null);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [filterTerms, setFilterTerms] = useState({ product: '', grade: '' });
  const [showModal, setShowModal] = useState(false);
  const [showModalMsg, setShowModalMsg] = useState('');  
  const [showRaiseQueryModal, setShowRaiseQueryModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState('');
  const [states, setStates] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const navigate = useNavigate();
  const pageSize = 10;
  const queryPageSize = 5;
  const { setLoading } = useLoading();

  const getBackendName = () => {
    const backend = config.aws_user_files_s3_bucket.split('-');
    return backend[backend.length - 1];
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const credentials = await Auth.currentCredentials();
      const lambdaClient = new LambdaClient({
        credentials: Auth.essentialCredentials(credentials),
        region: config.aws_project_region
      });
  
      const user = await Auth.currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;
      const backendName = getBackendName();
  
      const command = new InvokeCommand({
        FunctionName: `dataops-${backendName}`,
        Payload: JSON.stringify({
          body: {
            target: 'listProducts',
            access_token: token,
            rowStart: 0,
            pageSize: 100 
          }
        })
      });
  
      const data = await lambdaClient.send(command);
      let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));
      if (result.statusCode === 200) {
        result = JSON.parse(result.body);
        setProducts(result.items);
      } else {
        console.error('Error fetching products: ', result);
      }
    } catch (error) {
      console.error('Error fetching products', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPaymentOptions = async () => {
    setLoading(true);
    try {
      const credentials = await Auth.currentCredentials();
      const lambdaClient = new LambdaClient({
        credentials: Auth.essentialCredentials(credentials),
        region: config.aws_project_region
      });
  
      const user = await Auth.currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;
      const backendName = getBackendName();
  
      const command = new InvokeCommand({
        FunctionName: `dataops-${backendName}`,
        Payload: JSON.stringify({
          body: {
            target: 'listDropDownOptions',
            filterTerms: {
              "Type": "PaymentMode",
              "Subtype": "India"
            },
            access_token: token
          }
        })
      });
  
      const data = await lambdaClient.send(command);
      let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));
      if (result.statusCode === 200) {
        result = JSON.parse(result.body);
        setPaymentOptions(result.items);
      } else {
        console.error('Error fetching Payment Options: ', result);
      }
    } catch (error) {
      console.error('Error fetching Payment Options', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStates = async () => {
    setLoading(true);
    try {
      const credentials = await Auth.currentCredentials();
      const lambdaClient = new LambdaClient({
        credentials: Auth.essentialCredentials(credentials),
        region: config.aws_project_region
      });
  
      const user = await Auth.currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;
      const backendName = getBackendName();
  
      const command = new InvokeCommand({
        FunctionName: `dataops-${backendName}`,
        Payload: JSON.stringify({
          body: {
            target: 'listDropDownOptions',
            filterTerms: {
              "Type": "State",
              "Subtype": "India"
            },
            access_token: token
          }
        })
      });
  
      const data = await lambdaClient.send(command);
      let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));
      if (result.statusCode === 200) {
        result = JSON.parse(result.body);
        setAllStates(result.items);
      } else {
        console.error('Error fetching All States: ', result);
      }
    } catch (error) {
      console.error('Error fetching All States', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    setLoading(true);
    try {
      const credentials = await Auth.currentCredentials();
      const lambdaClient = new LambdaClient({
        credentials: Auth.essentialCredentials(credentials),
        region: config.aws_project_region
      });
  
      const user = await Auth.currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;
      const backendName = getBackendName();
  
      const command = new InvokeCommand({
        FunctionName: `dataops-${backendName}`,
        Payload: JSON.stringify({
          body: {
            target: 'listDynamicDropDownOptions',
            option: 'selleritemstates',
            access_token: token,
            rowStart: 0,
            pageSize: 100 
          }
        })
      });
  
      const data = await lambdaClient.send(command);
      let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));
      if (result.statusCode === 200) {
        result = JSON.parse(result.body);
        setStates(result.items.map(option => option.result));
      } else {
        console.error(`Error fetching $States : ${JSON.stringify(result)}`);
      }
    } catch (error) {
      console.error(`Error fetching States: ${JSON.stringify(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleProductChange = (event) => {
    let productName = event.target.value;
    setSelectedProductName(productName);
    let product = products.find(product => product.ProductName === productName);
    setSelectedGrade(product.Grade);
  };

  const handlePaymentOptionChange = (event) => {
    let paymentOption = event.target.value;
    setSelectedPaymentOption(paymentOption);
  };

  
  const fetchSellerItems = async () => {
    setLoading(true);
    try {
      const credentials = await Auth.currentCredentials();
      const lambdaClient = new LambdaClient({
        credentials: Auth.essentialCredentials(credentials),
        region: config.aws_project_region
      });
      const user = await Auth.currentAuthenticatedUser({
        bypassCache: true
      });

      const body = {
        target: "listSellerItemDetails",
        access_token: user.signInUserSession.idToken.jwtToken,
        filterTerms,
        rowStart: (currentPage - 1) * pageSize,
        pageSize: pageSize
      };

      const params = {
        FunctionName: `dataops-${getBackendName()}`,
        Payload: JSON.stringify({ body })
      };

      const command = new InvokeCommand(params);
      const data = await lambdaClient.send(command);
      let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));
      if (result.statusCode === 200) {
        result = JSON.parse(result.body);
        setSellerItemDetails(result.items);
        setTotalPages(Math.ceil(result.total / pageSize));
        if (result.items.length > 0) {
          setSelectedSellerItemDetail(result.items[0]);
          setSelectedProductName(result.items[0].ProductName);
          setSelectedGrade(result.items[0].Grade);
          setSelectedPaymentOption(result.items[0].Payment);
          setSelectedState(result.items[0].State);
          setIsAdding(false);
        }
      } else {
        console.error('Error fetching sellerItemDetails: ', result);
      }
    } catch (error) {
      console.error('Error invoking Lambda function: ', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQueries = async () => {
    if (!selectedSellerItemDetail) {
      console.error('No client selected');
      return;
    }
    setLoading(true);
    try {
      const credentials = await Auth.currentCredentials();
      const lambdaClient = new LambdaClient({
        credentials: Auth.essentialCredentials(credentials),
        region: config.aws_project_region
      });
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });

      const body = {
        target: 'listAmtplQueries',
        access_token: user.signInUserSession.idToken.jwtToken,
        filterTerms: {'SellerItemID': selectedSellerItemDetail?.SellerItemID},
        rowStart: (queryPage - 1) * queryPageSize,
        pageSize: queryPageSize
      };

      const params = {
        FunctionName: `dataops-${getBackendName()}`,
        Payload: JSON.stringify({ body })
      };

      const command = new InvokeCommand(params);
      const data = await lambdaClient.send(command);
      let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));
      if (result.statusCode === 200) {
        result = JSON.parse(result.body);
        setQueries(result.items);
        setQueryTotalPages(Math.ceil(result.total / queryPageSize));
        if (result.items.length > 0) {
          setSelectedQuery(result.items[0]);
        }
      } else {
        console.error('Error fetching queries: ', result);
      }
    } catch (error) {
      console.error('Error invoking Lambda function: ', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date, field) => {
    setSelectedSellerItemDetail({
      ...selectedSellerItemDetail,
      [field]: date ? date.toISOString().split('T')[0] : ''
    });
  };

  const handleCreateQuery = async () => {
    if (!queryDescription) {
      console.error('Query Description is required');
      return;
    }

    setLoading(true);
    try {
      const credentials = await Auth.currentCredentials();
      const lambdaClient = new LambdaClient({
        credentials: Auth.essentialCredentials(credentials),
        region: config.aws_project_region
      });
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });

      const body = {
        target: 'createAmtplQuery',
        access_token: user.signInUserSession.idToken.jwtToken,
        createAmtplQueryInput: {
          QueryDescription: queryDescription,
          SellerEmailID: user.attributes.email,
          SellerItemID: selectedSellerItemDetail?.SellerItemID,
          QueryType: 'Seller'
        }
      };

      const params = {
        FunctionName: `dataops-${getBackendName()}`,
        Payload: JSON.stringify({ body })
      };

      const command = new InvokeCommand(params);
      const data = await lambdaClient.send(command);
      let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));

      if (result.statusCode === 200) {
        console.log('Query creation successful', result);
        setShowModalMsg('Query creation successful');
        setShowModal(true);
        // Fetch the updated queries
        fetchQueries();
      } else {
        console.error('Error creating query: ', result);
        setShowModalMsg('Error creating query');
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error invoking Lambda function: ', error);
      setShowModalMsg('Error creating query');
      setShowModal(true);
    } finally {
      setLoading(false);
      setShowRaiseQueryModal(false); // Close the modal after handling the query creation
      setQueryDescription(''); // Clear the query description
    }
  };

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        // Check if the user is a member of the AMTPL_SELLERS_AND_FARMERS group
        const groups = decodedToken['cognito:groups'] || [];
        if (!groups.includes('AMTPL_SELLERS_AND_FARMERS')) {
          await Auth.signOut(); // Sign out the user
          navigate('/login/seller-farmer');
        } else {
          setActiveUser(user.attributes.email); // Set the active user email
        }
      } catch (error) {
        await Auth.signOut(); // Sign out the user
        navigate('/login/seller-farmer');
      }
    };

    checkUser();
    fetchSellerItems();
    if (activeTab === 'queries') {
      fetchQueries();
    }
  }, [navigate, currentPage, queryPage, setLoading, filterTerms]);

  useEffect(() => {
    if (activeTab === 'queries') {
      fetchQueries();
    }
  }, [selectedSellerItemDetail]);

  useEffect(() => {
    fetchProducts();
    fetchPaymentOptions();
    fetchStates();
    fetchAllStates();
  }, []);

  const handleGradeChange = (event) => {
    setSelectedGrade(event.target.value);
  };

  const handleStateChange = (event) => {
    setSelectedState(event.target.value);
  };
  
  const handleSignOut = async () => {
    setLoading(true);
    try {
      await Auth.signOut();
      navigate('/login/seller-farmer');
    } catch (error) {
      console.error('Error signing out: ', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabClick = (tab) => {
    if (tab === 'queries') {
      if(isAdding === true)
        return;
      fetchQueries();
    }
    setActiveTab(tab);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleQueryPageChange = (newPage) => {
    if (newPage > 0 && newPage <= queryTotalPages) {
      setQueryPage(newPage);
    }
  };

  const handleRowClick = (sellerItemDetail) => {
    setSelectedSellerItemDetail(sellerItemDetail);
    setSelectedProductName(sellerItemDetail.ProductName);
    setSelectedGrade(sellerItemDetail.Grade);
    setSelectedPaymentOption(sellerItemDetail.Payment);
    setSelectedState(sellerItemDetail.State);
    setIsAdding(false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterTerms((prevTerms) => ({ ...prevTerms, [name]: value }));
    resetFilter();
  };

  const handleSearch = () => {
    resetFilter();
  };

  const handleReset = () => {
    setFilterTerms({ product: '', grade: '' });
    resetFilter();
  };

  const handleQueryRowClick = (query) => {
    setSelectedQuery(query);
  };

  const handleSave = async () => {
    const product = products.find(
      product => product.ProductName === selectedProductName && product.Grade === selectedGrade
    );
    if (!selectedSellerItemDetail || !product) {
        console.error('No client selected');
        return;
    }

    let target = 'saveSellerItem', action = 'Save';
    if(isAdding) {
      target = 'addSellerItem';
      action = 'Add';
    }

    setLoading(true);
    try {
        const credentials = await Auth.currentCredentials();
        const lambdaClient = new LambdaClient({
            credentials: Auth.essentialCredentials(credentials),
            region: config.aws_project_region
        });
        const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
        
        const { ProductID, ProductName, Grade, Payment, State, ...rest } = selectedSellerItemDetail;
        
        const body = {
            target: target,
            access_token: user.signInUserSession.idToken.jwtToken,
            SellerItemExtendedInput: {
              ...rest,
              Payment: selectedPaymentOption,
              State: selectedState,
              ProductID: product.ProductID
            }
        };

        const params = {
            FunctionName: `backendops-${getBackendName()}`, // Updated to backendops
            Payload: JSON.stringify({ body })
        };

        const command = new InvokeCommand(params);
        const data = await lambdaClient.send(command);
        let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));

        if (result.statusCode === 200) {
            console.log(`${action} action successful`, result);
            setShowModalMsg(`${action} action successful`);
            setShowModal(true);
        } else {
            console.error(`Error updating Seller Item ${JSON.stringify(result)}`);
            setShowModalMsg(`Error updating Seller Item ${JSON.stringify(result)}`);
            setShowModal(true);
        }
    } catch (error) {
      console.error(`Error updating Seller Item ${JSON.stringify(error)}`);
      setShowModalMsg(`Error updating Seller Item ${JSON.stringify(error)}`);
      setShowModal(true);
    } finally {
      setIsAdding(false);
      setLoading(false);
      fetchSellerItems();
    }
  };

  const handleAdd = () => {
    setIsAdding(true);
    setSelectedSellerItemDetail({
      ProductName: '',
      Grade: '',
      Quantity: 0,
      Price: 0,
      State: '',
      Country: 'India',
      Expires: '',
      FFA: 0,
      Moisture: 0,
      OilContent: 0,
      ADmixture: 0,
      HarvestYear: '2024',
      Currency: 'INR',
      PricePerMT: 0,
      TotalPrice: 0,
      Payment: 'Cash'
    });
    setSelectedProductName('');
    setSelectedGrade('');
    setSelectedPaymentOption('');
    setSelectedState('');
  }
  
  function isValidNumber(value) {
    return value !== '' && !isNaN(value);
  }

  const handleSellerItemChange = (event) => {
    const { id, value } = event.target;
    const floatFields = [
      "Quantity",
      "Price",
      "FFA",
      "Moisture",
      "OilContent",
      "ADmixture",
      "PricePerMT",
      "TotalPrice"
    ];
    const intFields = [
    ];
    let newSellerItemDetail = {
      ...selectedSellerItemDetail
    };
    if (floatFields.includes(id)) {
      if(isValidNumber(value)) {
        newSellerItemDetail[id] = parseFloat(value);
      } else {
        newSellerItemDetail[id] = 0;
      }
    } else if (intFields.includes(id)) {
      if(isValidNumber(value)) {
        newSellerItemDetail[id] = parseInt(value);
      } else {
        newSellerItemDetail[id] = 0;
      }
    } else {
      newSellerItemDetail[id] = value;
    }
    setSelectedSellerItemDetail(newSellerItemDetail);
  }

  const resetFilter = () => {
    setSelectedSellerItemDetail(null);
    setSelectedProductName('');
    setSelectedGrade('');
    setSelectedPaymentOption('');
    setSelectedState('');
    setCurrentPage(1);
    setSelectedQuery(null);
    setQueryPage(1);
    setQueries([]);
    setIsAdding(false);
  }

  return (
    <div className="seller-dashboard">
      <header>
        <img src="/images/logo.png" alt="Grains Global Logo" className="logo" />
        <nav>
          <ul>
            <li><a href="/seller">Dashboard</a></li>
            <li>{activeUser}</li>
            <li><button onClick={handleSignOut} className="sign-out-button">Sign Out</button></li>
          </ul>
        </nav>
      </header>

      <div className="content">
        <main className="main-content">
          <div className='offers-search row'>
            <div className='col-lg-3 col-sm-4 col-6'>
                  <select className="form-control form-select" id="product" name="product" value={filterTerms.product || ''} onChange={handleFilterChange}>
                    <option value="">Select a product</option>
                    {[...new Set(products.map(product => product.ProductName))].map((product) => (
                      <option key={product} value={product}>{product}</option>
                    ))}
                  </select>
            </div>
            <div className='col-lg-3 col-sm-4 col-6'>
              <select className="form-control form-select" id="grade" name="grade" value={filterTerms.grade || ''} onChange={handleFilterChange}>
                <option value="">Select a Grade</option>
                {products.filter(product => product.ProductName === filterTerms.product).map((product) => (
                  <option key={product.Grade} value={product.Grade}>{product.Grade}</option>
                ))}
              </select>
            </div>
            <div className='col-lg-3 col-sm-4 col-6'>
              <select className="form-control form-select" id="state" name="state" value={filterTerms.state || ''} onChange={handleFilterChange}>
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div className='col-lg-3 col-sm-4 col-6'>
              <button onClick={handleSearch} className='mx-3 d-inline'>Search</button>
              <button onClick={handleReset} className='d-inline'>Reset</button>
            </div>
          </div>

          <section className="offers">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Grade</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Country</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {sellerItemDetails.map((sellerItemDetail) => (
                  <tr
                    key={sellerItemDetail.SellerItemID}
                    onClick={() => handleRowClick(sellerItemDetail)}
                    className={selectedSellerItemDetail && selectedSellerItemDetail?.SellerItemID === sellerItemDetail.SellerItemID ? 'selected' : ''}
                  >
                    <td>{sellerItemDetail.ProductName}</td>
                    <td>{sellerItemDetail.Grade}</td>
                    <td>{sellerItemDetail.Quantity}</td>
                    <td>{sellerItemDetail.Price}</td>
                    <td>{sellerItemDetail.Country}</td>
                    <td>{sellerItemDetail.Expires}</td>
                  </tr>
                ))}
                {sellerItemDetails.length === 0 && (
                  <tr>
                    <td colSpan="7">No Seller items available</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="pagination">
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>{'<'}</button>
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={currentPage === index + 1 ? 'active' : ''}
                >
                  {index + 1}
                </button>
              ))}
              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>{'>'}</button>
            </div>
          </section>

          <section className="product-details">
            <div className="tabs">
              <button
                className={activeTab === 'basicInfo' ? 'active' : ''}
                onClick={() => handleTabClick('basicInfo')}
              >
                Basic Info
              </button>
              <button
                className={activeTab === 'specifications' ? 'active' : ''}
                onClick={() => handleTabClick('specifications')}
              >
                Specifications
              </button>
              <button
                className={activeTab === 'pricing' ? 'active' : ''}
                onClick={() => handleTabClick('pricing')}
              >
                Pricing
              </button>
              <button
                className={activeTab === 'queries' ? 'active' : ''}
                onClick={() => handleTabClick('queries')}
              >
                Queries
              </button>
            </div>

            {activeTab === 'basicInfo' && (
              <div className="tab-content active">
                <div className='row'>
                  <div className='col-lg-9 col-sm-9 col-6'>
                    <div className='row'>
                      <div className='col-lg-3 col-sm-4 col-6'>
                        <label htmlFor="product">Product</label>
                        <select className="form-control form-select" id="product" value={selectedProductName || ''} onChange={handleProductChange}>
                          <option value="">Select a product</option>
                          {[...new Set(products.map(product => product.ProductName))].map((product) => (
                            <option key={product} value={product}>{product}</option>
                          ))}
                        </select>
                      </div>
                      <div className='col-lg-3 col-sm-4 col-6'>
                        <label htmlFor="grade">Grade</label>
                        <select className="form-control form-select" id="grade" value={selectedGrade || ''} onChange={handleGradeChange}>
                          <option value="">Select a Grade</option>
                          {products.filter(product => product.ProductName === selectedProductName).map((product) => (
                            <option key={product.Grade} value={product.Grade}>{product.Grade}</option>
                          ))}
                        </select>
                      </div>
                      <div className='col-lg-3 col-sm-4 col-6'>
                        <label>Quantity</label>
                        <input id="Quantity" type="text" value={selectedSellerItemDetail?.Quantity || ''} className='form-control' onChange={handleSellerItemChange}/>
                      </div>
                      <div className='col-lg-3 col-sm-4 col-6'>
                        <label>Price</label>
                        <input id="Price" type="text" value={selectedSellerItemDetail?.Price || ''} className='form-control' onChange={handleSellerItemChange}/>
                      </div>
                      <div className='col-lg-3 col-sm-4 col-6'>
                        <label>Country</label>
                        <input type="text" readOnly value={selectedSellerItemDetail?.Country || ''} className='form-control' />
                      </div>
                      <div className='col-lg-3 col-sm-4 col-6'>
                        <label htmlFor="state">State</label>
                        <select className="form-control form-select" id="State" value={selectedState} onChange={handleStateChange}>
                        <option value="">Select State</option>
                          {allStates.map((state) => (
                            <option key={state.Value} value={state.Value}>{state.Value}</option>
                          ))}
                        </select>
                      </div>
                      <div className='col-lg-3 col-sm-4 col-6'>
                        <label>Expires</label>
                        <DatePicker
                          id="Expires"
                          selected={selectedSellerItemDetail?.Expires ? new Date(selectedSellerItemDetail.Expires) : null}
                          onChange={(date) => handleDateChange(date, 'Expires')}
                          dateFormat="yyyy-MM-dd"
                          className='form-control'
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-3 col-sm-3 col-6'>
                    <div className='actions'>
                      <div className="row">
                        <div className='col-12 mb-2'><button disabled={isAdding} onClick={handleAdd}>Add</button></div>
                        <div className='col-12 mb-2'><button disabled={!(selectedSellerItemDetail || isAdding)} onClick={handleSave}>Save</button></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'specifications' && (
              <div className="tab-content active">
                <div className='row'>
                  <div className='col-lg-9 col-sm-9 col-6'>
                    <div className='row'>
                      <label htmlFor="productSpecifications">Product Specifications</label>
                      <textarea
                        id="productSpecifications"
                        name="productSpecifications"
                        maxLength="4000"
                        className="form-control"
                        rows="10"
                        value={selectedSellerItemDetail?.productSpecifications}
                        onChange={handleSellerItemChange}
                      />
                    </div>
                  </div>
                  <div className='col-lg-3 col-sm-3 col-6'>
                    <div className='actions'>
                      <div className="row">
                        <div className='col-12 mb-2'><button disabled={isAdding} onClick={handleAdd}>Add</button></div>
                        <div className='col-12 mb-2'><button disabled={!(selectedSellerItemDetail || isAdding)} onClick={handleSave}>Save</button></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'pricing' && (
              <div className="tab-content active">
                <div className='row'>
                  <div className='col-lg-9 col-sm-9 col-6'>
                    <div className='row'>
                      <div className='col-lg-3 col-sm-4 col-6'>
                        <label>Currency</label>
                        <input id="Currency" type="text" readOnly value={selectedSellerItemDetail?.Currency || ''} className='form-control'/>
                      </div>
                      <div className='col-lg-3 col-sm-4 col-6'>
                        <label>Price / MT</label>
                        <input type="text" readOnly value={selectedSellerItemDetail?.Price || ''} className='form-control' />
                      </div>
                      <div className='col-lg-3 col-sm-4 col-6'>
                        <label>Total Price</label>
                        <input type="text" readOnly value={(selectedSellerItemDetail?.Price * selectedSellerItemDetail?.Quantity) || ''} className='form-control' />
                      </div>
                      <div className='col-lg-3 col-sm-4 col-6'>
                        <label htmlFor="payment">Payment</label>
                        <select className="form-control form-select" id="payment" value={selectedPaymentOption} onChange={handlePaymentOptionChange}>
                        <option value="">Select Payment</option>
                          {paymentOptions.map((paymentOption) => (
                            <option key={paymentOption.Value} value={paymentOption.Value}>{paymentOption.Value}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-3 col-sm-3 col-6'>
                    <div className='actions'>
                      <div className="row">
                        <div className='col-12 mb-2'><button disabled={isAdding} onClick={handleAdd}>Add</button></div>
                        <div className='col-12 mb-2'><button disabled={!(selectedSellerItemDetail || isAdding)} onClick={handleSave}>Save</button></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'queries' && (
              <div className="tab-content active">
                <div className='row'>
                  <div className='col-lg-10 col-sm-9 col-12'>
                    <table>
                      <thead>
                        <tr>
                        <th>Query Date</th>
                          <th>Query</th>
                          <th>Reply Date</th>
                          <th>Reply</th>
                        </tr>
                      </thead>
                      <tbody>
                        {queries.map((query) => (
                          <tr
                            key={query.QueryID}
                            onClick={() => handleQueryRowClick(query)}
                            className={selectedQuery && selectedQuery.QueryID === query.QueryID ? 'selected' : ''}
                          >
                            <td>{query.QueryDate}</td>
                            <td>{query.QueryDescription}</td>
                            <td>{query.QueryReplyDate}</td>
                            <td>{query.QueryReply}</td>
                          </tr>
                        ))}
                        {queries.length === 0 && (
                          <tr>
                            <td colSpan="4">No queries available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className='col-lg-2 col-sm-3 col-12'>
                    <div className='actions'>
                      <button disabled={selectedSellerItemDetail === undefined || selectedSellerItemDetail === null} onClick={() => {
                        setShowRaiseQueryModal(true);
                      }}>Raise Query</button>
                    </div>
                  </div>
                </div>
                <div className="pagination">
                  <button onClick={() => handleQueryPageChange(queryPage - 1)} disabled={queryPage === 1}>{'<'}</button>
                  {Array.from({ length: queryTotalPages }, (_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => handleQueryPageChange(index + 1)}
                      className={queryPage === index + 1 ? 'active' : ''}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button onClick={() => handleQueryPageChange(queryPage + 1)} disabled={queryPage === queryTotalPages}>{'>'}</button>
                </div>
              </div>
            )}
          </section>
        </main>

        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Message</Modal.Title>
          </Modal.Header>
          <Modal.Body>{showModalMsg}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
        
        <Modal show={showRaiseQueryModal} onHide={() => setShowRaiseQueryModal(false)} backdrop='static' className='modal-raisequery'>
          <Modal.Header closeButton>
            <Modal.Title>Raise Query</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='row'>
              <div className='col-12'>
                <textarea
                  placeholder='Enter Query Description'
                  className='form-control mb-2'
                  value={queryDescription}
                  onChange={(e) => setQueryDescription(e.target.value)}
                ></textarea>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleCreateQuery}>
              Raise Query
            </Button>
            <Button variant="secondary" onClick={() => setShowRaiseQueryModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default SellerFarmerDashboard;